import React, { useRef, useState, useEffect } from 'react';
import styled from 'styled-components';
import { formatTime } from '../shared/utils/helper';

interface TimelineRulerProps {
  height?: number;
  min?: number;
  max?: number;
  mainStep?: number;
  smallStepCount?: number;
  progress?: number;
}

const RulerContainer = styled.div<{ height: number }>`
  position: relative;
  width: 100%;
  height: ${props => props.height}px;
  border-top: 2px solid #B5B5B5;
`;

const TickMark = styled.div<{ 
  position: number; 
  isMain: boolean 
}>`
  position: absolute;
  top: 0;
  left: ${props => props.position}%;
  height: ${props => props.isMain ? '25px' : '8px'};
  border-left-width: ${props => props.isMain ? '2px' : '1px'};
  border-left-style: solid;
  border-left-color: #B5B5B5;
`;

const TickLabel = styled.span`
  position: absolute;
  top: 40%;
  left: 20px;
  transform: translateX(-50%);
  font-size: 0.75rem;
  color: #B5B5B5;
`;

const TimeMarker = styled.div<{ position: number }>`
  position: absolute;
  left: ${(props) => props.position? `${props.position}%` : '0px'};
  top: -2px;
  height: 27px;
  width: 2px;
  background: red;
  z-index: 99999999999;
`;

const Timeline: React.FC<TimelineRulerProps> = ({
  height = 50,
  min = 0,
  max = 100,
  mainStep = 10,
  smallStepCount = 4,
  progress = 0
}) => {
  const [currentTime, setCurrentTime] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    const updateWidth = () => {
      if (containerRef.current) {
        setWidth(containerRef.current.offsetWidth);
      }
    };

    // Initial width
    updateWidth();

    // Add resize listener
    window.addEventListener('resize', updateWidth);

    // Cleanup
    return () => window.removeEventListener('resize', updateWidth);
  }, []);

  const generateTicks = () => {
    const ticks: { value: number; type: 'main' | 'small' }[] = [];
    
    for (let i = min; i <= max; i += mainStep) {
      // Main marker
      ticks.push({ value: i, type: 'main' });
      
      // Small markers between main markers
      if (i + mainStep <= max) {
        const stepSize = mainStep / (smallStepCount + 1);
        for (let j = 1; j <= smallStepCount; j++) {
          ticks.push({ 
            value: i + (j * stepSize), 
            type: 'small' 
          });
        }
      }
    }
    
    return ticks;
  };

  const calculateTickPosition = (value: number) => {
    return ((value - min) / (max - min)) * 100;
  };

  useEffect(() => {
    if (containerRef.current) {
      containerRef.current.scrollLeft = (currentTime / max) * containerRef.current.scrollWidth;
    }
  }, [currentTime, max]);

  const handleClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (containerRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const clickPosition = (event.clientX - rect.left) / rect.width;
      setCurrentTime(clickPosition * max);
    }
  };

  useEffect(()=>{
    setCurrentTime(progress)
  }, [progress])


  return (
    <RulerContainer ref={containerRef} height={height} onClick={handleClick}>
      <TimeMarker position={(currentTime / max) * 100} />
      {generateTicks().map((tick, index) => (
        <TickMark 
          key={`${tick.value}-${index}`}
          position={calculateTickPosition(tick.value)}
          isMain={tick.type === 'main'}
        >
          {tick.type === 'main' && (
            <TickLabel>{formatTime(tick.value)}</TickLabel>
          )}
        </TickMark>
      ))}
    </RulerContainer>
  );
};

export default Timeline;